import UaParser from 'ua-parser-js';

import { getEnv } from '@luna/env';
import { logger } from '@luna/logger';
import { request } from '@luna/api';
import { getFeatureConfig } from '@luna/feature-config';
import { getLuxIdActorParameter, selectMyProfileData } from '@shared/profile';
import { isFeatureEnabled } from '@luna/gatekeeper';
import _ from '@luna/lodash';
import type { COUNTRY } from '@shared/core-types';
import { getCurrentLanguage } from '@luna/i18n';

import type { Tracker } from '../model/Tracker';
import type { TrackingEvent } from '../model/TrackingEvent';
import type { BaseTrackEvent } from '../model/trackingTypes';
import { EventQueue } from './EventQueue';
import { getVersion } from '../utils/TrackingService';
import { shouldTrackEventHistory } from './helpers/shouldTrackEventHistory';

interface Detail {
    codKey: string;
    txtValue: string;
}

const KNOWN_LUX_PROPERTIES = new Set([
    'idTrackingPrev',
    'idTracking',
    'codTouchpoint',
    'codTrackingType',
    'domainUserid',
    'seTag',
    'seLabel',
    'seProperty',
    'skipIdActor',
    'mktCampaign',
    'idMktCustomizedCampaign',
    'idMktImpression',
    'idMktReaction',
    'eventKey',
    'binTextBlock',
    'idCase',
    'codProductFamily',
    'codProductName',
    'codDataProtection',
]);

interface LuxDto {
    nameTracker: string;
    vTracker: string;
    appId: string;
    trueTstamp: string;
    idActor?: string;
    event: string;
    codEventKey: string;
    seAction: string;

    idLogin?: string;
    numTenant?: number;

    seLabel?: string;
    seProperty?: string;

    mktCampaign?: string;
    idMktCustomizedCampaign?: string;
    idMktImpression?: string;
    idMktReaction?: string;
    idTrackingPrev?: string;
    idTracking?: string;
    seTag?: string;

    pageUrl?: string;
    // web ios android
    osFamily?: string;
    osName?: string;
    dvceType?: string;
    // session id
    domainSessionid?: string;
    // country code
    codTrackerCountry?: string;

    codKey?: string;
    binTextBlock?: string;
    codValue?: string;
    idLicenceHolder?: string;
    details?: Detail[];
    codTouchpoint: 'george' | 'bot' | 'unknown';
    codTrackingType?: string;
    codChannel: 'online';
    codUserLanguage: string;
}

function getLuxUrl(
    luxUrl: string | undefined,
    luxProxyUrl: string | undefined,
    openLuxProxyUrl: string | undefined,
    shouldUseLuxProxy: boolean,
    shouldUseOpenLuxProxy: boolean,
): string {
    if (!shouldUseLuxProxy) {
        return `${luxUrl}/lux-collector-internet/rest/atomicluxevent`;
    }

    if (shouldUseOpenLuxProxy) {
        return `${openLuxProxyUrl}/atomicluxevent`;
    }

    return `${luxProxyUrl}/atomicluxevent`;
}

function extractKnownProperties(payload: Record<string, any>) {
    const knownProps = {} as Record<string, any> & {
        codEventKey: string;
        codTouchpoint?: string;
        binTextBlock?: string;
    };
    const remainingProps: Record<string, any> = {};

    Object.entries(payload).forEach(([key, value]) => {
        if (KNOWN_LUX_PROPERTIES.has(key)) {
            if (key === 'eventKey') {
                // rename eventKey to codEventKey as required by Lux endpoint
                knownProps.codEventKey = value;
                return;
            }
            if (key === 'codTouchpoint') {
                knownProps.codTouchpoint = value;
                return;
            }
            if (key === 'binTextBlock') {
                knownProps.binTextBlock = value;
                return;
            }
            if (key === 'skipIdActor') {
                // skipIdActor is not sent to Lux
                return;
            }

            knownProps[key] = value;
        } else {
            remainingProps[key] = value;
        }
    });

    return { knownProps, remainingProps };
}

function mapPayloadToDetails(payload?: Record<string, any>): Detail[] {
    if (!payload) return [];

    const filteredPayload = { ...payload };

    delete filteredPayload.eventType;
    delete filteredPayload.eventKey;
    delete filteredPayload.category;
    delete filteredPayload.name;

    KNOWN_LUX_PROPERTIES.forEach((prop) => {
        delete filteredPayload[prop];
    });

    return Object.entries(filteredPayload)
        .map(([key, value]) => ({
            codKey: key,
            txtValue: value?.toString(),
        }))
        .filter((item) => {
            if (!item.txtValue) {
                return false;
            }

            return true;
        });
}

const transformAppName = (appName: string | undefined, country: COUNTRY) => {
    if (!appName) {
        return undefined;
    }
    if (country && appName.endsWith(`_${country}`)) {
        return appName.substring(0, appName.lastIndexOf('_'));
    }
    return appName;
};

export interface LuxTrackerMiddleware {
    willSendDTO?(dto: LuxDto, event: TrackingEvent<BaseTrackEvent>): LuxDto;
}

export class LuxTracker implements Tracker {
    static queue = new EventQueue();

    private middlewares: LuxTrackerMiddleware[] = [];

    eventHistory: TrackingEvent<BaseTrackEvent>[] = [];

    addMiddleware(middleware: LuxTrackerMiddleware) {
        this.middlewares.push(middleware);
    }

    removeMiddleware(middleware: LuxTrackerMiddleware) {
        this.middlewares = this.middlewares.filter((item) => item !== middleware);
    }

    async track(event: TrackingEvent<BaseTrackEvent>): Promise<void> {
        if (LuxTracker.queue.isStopped) {
            return;
        }
        if (shouldTrackEventHistory()) {
            this.eventHistory.push(event);
        }
        if (!isFeatureEnabled((b) => b.luxTracking)) {
            return;
        }
        const { eventType, eventKey, skipIdActor = false } = event.payload;
        if (!eventType || !eventKey) {
            return;
        }
        const { luxUrl, luxApiSourceID, luxProxyUrl, openLuxProxyUrl } = getEnv();
        if (!(luxUrl || luxProxyUrl) || !luxApiSourceID) {
            logger.debug('Lux url or lux api source id are missing from the environment config.');
            return;
        }
        const country = getFeatureConfig((f) => f.base?.country);
        const appName = transformAppName(
            getFeatureConfig((f) => f.tracking?.appName),
            country,
        );
        if (!appName) {
            logger.debug('Lux configuration "appName" is missing');
            return;
        }
        const appId = getFeatureConfig((f) => f.tracking?.appId);
        if (!appId) {
            logger.debug('Lux configuration "appId" is missing');
            return;
        }
        const instituteIdConfig = getFeatureConfig((f) => f.tracking?.instituteId);

        const nameSnakeCase = _.snakeCase(event.name);

        const { knownProps, remainingProps } = extractKnownProperties(event.payload);
        const details = mapPayloadToDetails(remainingProps);
        const uaParser = new UaParser();
        const os = uaParser.getOS();
        const osName = `${os.name} ${os.version}`.trim();

        const body: LuxDto = {
            nameTracker: appName,
            appId,
            vTracker: getVersion(),
            trueTstamp: new Date(event.timestamp).toISOString(),
            numTenant: instituteIdConfig ?? selectMyProfileData()?.instituteId ?? 0,
            event: event.payload?.eventType,
            seAction: nameSnakeCase,
            domainSessionid: event.sessionId,
            pageUrl: event.source,
            codTrackerCountry: getFeatureConfig((c) => c.base.country),
            osFamily: 'web',
            osName,
            codChannel: 'online',
            codUserLanguage: getCurrentLanguage(),
            details,
            ...knownProps,
            codTouchpoint: (knownProps.codTouchpoint as LuxDto['codTouchpoint']) || 'george',
        };

        const idLogin = selectMyProfileData()?.userNumber;

        if (idLogin !== undefined) {
            body.idLogin = idLogin;
        }

        const idActor = getLuxIdActorParameter();
        if (idActor !== undefined && !skipIdActor) {
            body.idActor = idActor;
        }

        const licenseHolder = selectMyProfileData()?.companyInfo?.licenseHolder;
        if (licenseHolder) {
            body.idLicenceHolder = String(licenseHolder);
        }

        const bodyAfterMiddlewares = this.middlewares.reduce((result, middleware) => {
            if (!middleware.willSendDTO) {
                return result;
            }

            return middleware.willSendDTO(result, event);
        }, body);

        const shouldUseLuxProxy =
            isFeatureEnabled((features) => features?.luxProxy ?? { enabled: false }) &&
            !!luxProxyUrl;

        const shouldUseOpenLuxProxy =
            isFeatureEnabled((features) => features?.openLuxProxy ?? { enabled: false }) &&
            !!openLuxProxyUrl;

        const eventBuilder = (): Promise<void> =>
            request(
                getLuxUrl(
                    luxUrl,
                    luxProxyUrl,
                    openLuxProxyUrl,
                    shouldUseLuxProxy,
                    shouldUseOpenLuxProxy,
                ),
                {
                    body: bodyAfterMiddlewares,
                    method: 'POST',
                    headers: {
                        'Content-Type':
                            'application/vnd.at.spardat.misol.lux.entities.events.atomic.lux.event+json',
                        ApiKey: luxApiSourceID,
                    },
                    skipAuthHeader: !shouldUseLuxProxy || shouldUseOpenLuxProxy,
                    priority: 'low',
                    credentials: shouldUseLuxProxy ? 'include' : 'omit',
                },
            );

        await LuxTracker.queue.track(eventBuilder);
    }

    flushQueue(): Promise<void> {
        return LuxTracker.queue.flushQueue();
    }

    reset(): void {
        return LuxTracker.queue.reset();
    }

    stop(): void {
        return LuxTracker.queue.stop();
    }

    resetHistory(): void {
        this.eventHistory = [];
    }
}
